'use client';

import { useEffect, useId, useState, type FormEvent } from 'react';
import {
  AccountError,
  useCommerceSource,
  type AccountProfile,
  type ProductReview,
  type ProductReviewPage,
  type ReviewReceipt,
} from '@akademi/site-builder-runtime';
import { ReviewStars, StarIcon } from './ReviewStars';

/**
 * The reviews section of the product page: what other shoppers said, and a form to say something.
 *
 * <p>The first page arrives with the product page's own render, so the reviews are in the HTML a
 * crawler reads and match the rating in the structured data. Only "show more" and submitting go
 * through the browser's `CommerceSource`.
 *
 * <p>Strings come in as `labels` — the `reviews.*` slice of the app's catalogue — rather than via
 * `lib/messages`, which would ship every language's catalogue to the browser with this component.
 */

type Labels = Record<string, string>;

const MIN_BODY = 10;
const MAX_BODY = 4000;
const PAGE_SIZE = 5;
const PLAUSIBLE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function say(labels: Labels, key: string, params: Record<string, string | number> = {}): string {
  const template = labels[key];
  if (template == null) return key;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

export function ProductReviews({
  productId,
  initial,
  labels,
  locale,
  timeZone,
}: {
  productId: number;
  initial: ProductReviewPage | null;
  labels: Labels;
  locale: string;
  timeZone?: string;
}) {
  const source = useCommerceSource();
  const [items, setItems] = useState<ProductReview[]>(initial?.items ?? []);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [writing, setWriting] = useState(false);
  const [receipt, setReceipt] = useState<ReviewReceipt | null>(null);

  const t = (key: string, params?: Record<string, string | number>) => say(labels, key, params);
  const summary = initial?.summary;
  const count = summary?.count ?? 0;
  const total = initial?.total ?? 0;
  const distribution = summary?.distribution ?? [0, 0, 0, 0, 0];

  const dateFormat = (() => {
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) });
    } catch {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });
    }
  })();

  async function loadMore() {
    if (!source?.productReviews) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const next = await source.productReviews(productId, page + 1, PAGE_SIZE);
      if (next) {
        setItems((current) => {
          const seen = new Set(current.map((review) => review.id));
          return [...current, ...next.items.filter((review) => !seen.has(review.id))];
        });
        setPage(page + 1);
      }
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="reviews" aria-labelledby="reviews-heading" className="scroll-mt-24">
      <h2 id="reviews-heading" className="mb-4 text-xl font-medium">
        {t('reviews.heading')}
      </h2>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          {count > 0 && summary?.average ? (
            <>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-semibold">{summary.average}</span>
                <div className="flex flex-col gap-1">
                  <ReviewStars
                    rating={Number(summary.average)}
                    label={t('reviews.average', { average: summary.average })}
                    className="h-5 w-5"
                  />
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {count === 1 ? t('reviews.countOne') : t('reviews.count', { count })}
                  </span>
                </div>
              </div>
              <ul aria-label={t('reviews.distributionLabel')} className="flex flex-col gap-1.5">
                {[5, 4, 3, 2, 1].map((stars) => {
                  const rows = distribution[stars - 1] ?? 0;
                  return (
                    <li key={stars} className="flex items-center gap-2 text-sm">
                      <span className="sr-only">{t('reviews.distributionRow', { stars, count: rows })}</span>
                      <span aria-hidden="true" className="w-6 text-right tabular-nums">
                        {stars}★
                      </span>
                      <span
                        aria-hidden="true"
                        className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-neutral-100,#f3f4f6)]"
                      >
                        <span
                          className="block h-full rounded-full bg-amber-500"
                          style={{ width: `${count ? (rows / count) * 100 : 0}%` }}
                        />
                      </span>
                      <span aria-hidden="true" className="w-8 tabular-nums text-[var(--color-text-secondary)]">
                        {rows}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <div>
              <p className="font-medium">{t('reviews.none')}</p>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('reviews.beFirst')}</p>
            </div>
          )}

          {source?.submitReview ? (
            receipt ? (
              <p role="status" className="rounded-md bg-[var(--color-surface-muted,#f3f4f6)] p-3 text-sm">
                {receipt.verifiedPurchase ? t('reviews.thanksVerified') : t('reviews.thanks')}
              </p>
            ) : writing ? null : (
              <button
                type="button"
                onClick={() => setWriting(true)}
                className="inline-flex min-h-11 items-center justify-center rounded-md border border-[var(--color-border-default)] px-4 font-medium focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                {t('reviews.write')}
              </button>
            )
          ) : null}
        </div>

        <div className="flex flex-col gap-6">
          {writing && !receipt ? (
            <ReviewForm
              productId={productId}
              labels={labels}
              onCancel={() => setWriting(false)}
              onSubmitted={(submitted) => {
                setReceipt(submitted);
                setWriting(false);
              }}
            />
          ) : null}

          {items.length ? (
            <ul className="flex flex-col">
              {items.map((review) => (
                <li
                  key={review.id}
                  className="border-b border-[var(--color-border-subtle,#e5e7eb)] py-5 first:pt-0"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <ReviewStars
                      rating={review.rating}
                      label={t('reviews.starsLabel', { rating: review.rating })}
                    />
                    {review.title ? <h3 className="font-medium">{review.title}</h3> : null}
                  </div>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-[var(--color-text-secondary)]">
                    <span>{review.displayName || t('reviews.anonymous')}</span>
                    {review.createdAt ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <time dateTime={review.createdAt}>{dateFormat.format(new Date(review.createdAt))}</time>
                      </>
                    ) : null}
                    {review.verifiedPurchase ? (
                      <span className="inline-flex items-center gap-1 font-medium text-[var(--color-success-500,#16a34a)]">
                        <span aria-hidden="true">✓</span>
                        {t('reviews.verified')}
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-2 whitespace-pre-line break-words">{review.body}</p>
                  {review.reply ? (
                    <div className="mt-3 rounded-md bg-[var(--color-surface-muted,#f3f4f6)] p-3 text-sm">
                      <p className="font-medium">{t('reviews.reply')}</p>
                      <p className="mt-1 whitespace-pre-line break-words">{review.reply}</p>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {source?.productReviews && items.length < total ? (
            <div>
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loading}
                className="inline-flex min-h-11 items-center rounded-md border border-[var(--color-border-default)] px-4 disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                {t('reviews.showMore')}
              </button>
              {loadFailed ? (
                <p role="alert" className="mt-2 text-sm text-[var(--color-danger-600,#dc2626)]">
                  {t('reviews.loadError')}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ReviewForm({
  productId,
  labels,
  onCancel,
  onSubmitted,
}: {
  productId: number;
  labels: Labels;
  onCancel: () => void;
  onSubmitted: (receipt: ReviewReceipt) => void;
}) {
  const source = useCommerceSource();
  const id = useId();
  const t = (key: string, params?: Record<string, string | number>) => say(labels, key, params);

  // undefined while we find out; null for a guest.
  const [profile, setProfile] = useState<AccountProfile | null | undefined>(undefined);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    if (!source?.accountProfile) {
      setProfile(null);
      return;
    }
    source
      .accountProfile()
      .then((signedIn) => live && setProfile(signedIn))
      .catch(() => live && setProfile(null));
    return () => {
      live = false;
    };
  }, [source]);

  const guest = profile === null;

  function problem(): string | null {
    if (rating < 1) return t('reviews.error.rating');
    if (body.trim().length < MIN_BODY) return t('reviews.error.body', { min: MIN_BODY });
    if (guest && !name.trim()) return t('reviews.error.name');
    if (guest && !PLAUSIBLE_EMAIL.test(email.trim())) return t('reviews.error.email');
    return null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!source?.submitReview || profile === undefined) return;
    const refused = problem();
    setError(refused);
    if (refused) return;
    setBusy(true);
    try {
      const receipt = await source.submitReview({
        productId,
        rating,
        body: body.trim(),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(name.trim() ? { displayName: name.trim() } : {}),
        ...(guest ? { email: email.trim() } : {}),
      });
      onSubmitted(receipt);
    } catch (failure) {
      const kind = failure instanceof AccountError ? failure.kind : 'UNAVAILABLE';
      setError(
        kind === 'EMAIL_TAKEN'
          ? t('reviews.error.taken')
          : kind === 'RATE_LIMITED'
            ? t('reviews.error.rateLimited')
            : t('reviews.error.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  const field =
    'mt-1 w-full rounded-md border border-[var(--color-border-default)] bg-transparent px-3 py-2 focus-visible:outline-2 focus-visible:outline-offset-1';

  return (
    <form
      onSubmit={(event) => void submit(event)}
      noValidate
      aria-labelledby={`${id}-heading`}
      className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--color-border-subtle,#e5e7eb)] p-4"
    >
      <h3 id={`${id}-heading`} className="text-lg font-medium">
        {t('reviews.form.heading')}
      </h3>

      <fieldset>
        <legend className="text-sm font-medium">{t('reviews.form.rating')}</legend>
        <div className="mt-1 flex gap-1" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((value) => (
            <label key={value} className="cursor-pointer" onMouseEnter={() => setHover(value)}>
              <input
                type="radio"
                name={`${id}-rating`}
                value={value}
                checked={rating === value}
                onChange={() => setRating(value)}
                className="peer sr-only"
              />
              <span className="sr-only">
                {value === 1 ? t('reviews.form.starOne') : t('reviews.form.star', { stars: value })}
              </span>
              <StarIcon
                className={`h-8 w-8 rounded peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 ${
                  (hover || rating) >= value
                    ? 'text-amber-500'
                    : 'text-[var(--color-neutral-200,#e5e7eb)]'
                }`}
              />
            </label>
          ))}
        </div>
      </fieldset>

      {/* Hints sit outside their labels and are joined by aria-describedby, so a field's name
          is its label alone — "Your review", not the label and the hint read as one sentence. */}
      <div>
        <label htmlFor={`${id}-title`} className="block text-sm font-medium">
          {t('reviews.form.title')}
        </label>
        <input
          id={`${id}-title`}
          type="text"
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          className={field}
        />
      </div>

      <div>
        <label htmlFor={`${id}-body`} className="block text-sm font-medium">
          {t('reviews.form.body')}
        </label>
        <textarea
          id={`${id}-body`}
          value={body}
          required
          rows={5}
          maxLength={MAX_BODY}
          aria-describedby={`${id}-body-hint`}
          onChange={(event) => setBody(event.target.value)}
          className={field}
        />
        <p id={`${id}-body-hint`} className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('reviews.form.bodyHint', { min: MIN_BODY })}
        </p>
      </div>

      {profile === undefined ? null : (
        <>
          <div>
            <label htmlFor={`${id}-name`} className="block text-sm font-medium">
              {guest ? t('reviews.form.name') : t('reviews.form.nameOptional')}
            </label>
            <input
              id={`${id}-name`}
              type="text"
              value={name}
              maxLength={100}
              required={guest}
              autoComplete="nickname"
              onChange={(event) => setName(event.target.value)}
              className={field}
            />
          </div>
          {guest ? (
            <div>
              <label htmlFor={`${id}-email`} className="block text-sm font-medium">
                {t('reviews.form.email')}
              </label>
              <input
                id={`${id}-email`}
                type="email"
                value={email}
                required
                maxLength={200}
                autoComplete="email"
                aria-describedby={`${id}-email-hint`}
                onChange={(event) => setEmail(event.target.value)}
                className={field}
              />
              <p id={`${id}-email-hint`} className="mt-1 text-sm text-[var(--color-text-secondary)]">
                {t('reviews.form.emailHint')}
              </p>
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              {t('reviews.form.signedInAs', { email: profile.email })}
            </p>
          )}
        </>
      )}

      {error ? (
        <p role="alert" className="text-sm text-[var(--color-danger-600,#dc2626)]">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={busy || profile === undefined}
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--color-brand-500)] px-4 font-medium text-[var(--color-text-on-brand)] disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {busy ? t('reviews.form.submitting') : t('reviews.form.submit')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-11 items-center rounded-md px-4 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {t('reviews.form.cancel')}
        </button>
      </div>
    </form>
  );
}
